import React from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   Schedule Change Log — shared token components + field-tone map
   Used by both ScheduleLogPage (By PM overview) and ScheduleLogTab (By Project).
   The real API sends Title-Case `field_changed` labels, so the map is keyed on
   those exact strings.
   ═══════════════════════════════════════════════════════════════════════════ */

export const fmtDate = (v) => {
  if (v == null || v === '') return '—';
  const d = v instanceof Date ? v : new Date(String(v).length <= 10 ? `${v}T00:00:00` : v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const fmtTime = (d) => {
  if (d == null) return '—';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

export const dayDiff = (oldV, newV) => {
  const a = new Date(`${oldV}T00:00:00`), b = new Date(`${newV}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b - a) / 86400000);
};

/* ─────────────── field-tone map (keyed on real Title-Case labels) ─────────────── */

const FIELD_META = {
  'Start Date': { tone: 'var(--accent)', text: 'var(--accent-text)', icon: 'calendar', isDate: true },
  'Finish Date': { tone: 'var(--danger)', text: 'var(--danger)', icon: 'calendar', isDate: true },
  'Duration': { tone: 'var(--warning)', text: 'var(--warning)', icon: 'clock' },
  'Percent Complete': { tone: 'var(--success)', text: 'var(--success)', icon: 'bar-chart' },
  'Status': { tone: 'var(--type-inspection)', text: 'var(--type-inspection)', icon: 'tag' },
  'Responsible Party': { tone: 'var(--text-3)', text: 'var(--text-2)', icon: 'user', neutral: true },
};
export const fieldMeta = (label) => ({ label, ...(FIELD_META[label] || { tone: 'var(--text-3)', text: 'var(--text-2)', icon: 'edit', neutral: true }) });
const isDateField = (label) => label === 'Start Date' || label === 'Finish Date';
export const fmtVal = (label, val) => {
  if (val == null || val === '') return '(empty)';
  return isDateField(label) ? fmtDate(val) : String(val);
};
export const fieldSoft = (f) => (f.neutral ? 'var(--bg-surface-2)' : `color-mix(in oklch, ${f.tone} 14%, var(--bg-surface))`);
export const fieldRing = (f) => (f.neutral ? 'var(--border-subtle)' : `color-mix(in oklch, ${f.tone} 22%, transparent)`);

/* ─────────────── icons ─────────────── */

export const SlIcon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'calendar':
      return p(<><rect x="3" y="4.5" width="18" height="16.5" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>);
    case 'clock':
      return p(<><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></>);
    case 'bar-chart':
      return p(<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />);
    case 'tag':
      return p(<><path d="M3 7v5.2a2 2 0 0 0 .6 1.4l7.6 7.6a2 2 0 0 0 2.8 0l5.4-5.4a2 2 0 0 0 0-2.8L11.8 5.4A2 2 0 0 0 10.4 4.8L5 4.8A2 2 0 0 0 3 7Z" /><circle cx="7.5" cy="8.5" r="1.2" fill="currentColor" stroke="none" /></>);
    case 'user':
      return p(<><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>);
    case 'edit':
      return p(<><path d="M4 20h4L19 9l-4-4L4 16Z" /><path d="m13.5 6.5 4 4" /></>);
    case 'arrow-r':
      return p(<path d="M5 12h14M13 6l6 6-6 6" />);
    case 'chevron-r':
      return p(<path d="m9 6 6 6-6 6" />);
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    case 'arrow-up-r':
      return p(<><path d="M7 17 17 7" /><path d="M8 7h9v9" /></>);
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'inbox':
      return p(<><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" /></>);
    default:
      return null;
  }
};

/* ─────────────── count chip ─────────────── */

export const CountChip = ({ children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 28, padding: '0 12px', borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{children}</span>
);

/* ─────────────── tokenized select ─────────────── */

export const TokenSelect = ({ value, onChange, options, placeholder, minWidth = 220 }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="sl-select"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 34, padding: '0 34px 0 13px', borderRadius: 8, minWidth, fontSize: 14.5, fontWeight: 500, color: value ? 'var(--text-1)' : 'var(--text-4)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em' }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 11, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><SlIcon name="chevron-d" size={14} /></span>
  </div>
);

/* ─────────────── inline field badge + chip ─────────────── */

export const FieldBadge = ({ field }) => {
  const f = fieldMeta(field);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 9px 0 7px', borderRadius: 6, background: fieldSoft(f), border: `1px solid ${fieldRing(f)}`, fontSize: 13, fontWeight: 600, color: f.text, whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', color: f.text }}><SlIcon name={f.icon} size={12} sw={1.8} /></span>
      {field}
    </span>
  );
};

export const FieldChip = ({ field, count, selected = false, onClick }) => {
  const f = fieldMeta(field);
  const clickable = !!onClick;
  return (
    <button type="button" onClick={onClick} disabled={!clickable} className={clickable ? 'sl-fieldchip' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 14px', borderRadius: 10, textAlign: 'left', background: selected ? fieldSoft(f) : 'var(--bg-surface)', border: `1px solid ${selected ? fieldRing(f) : 'var(--border-default)'}`, boxShadow: selected ? 'none' : 'var(--shadow-xs)', cursor: clickable ? 'pointer' : 'default', transition: 'background 120ms, border-color 120ms' }}>
      <span style={{ width: 30, height: 30, borderRadius: 8, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: f.text, background: fieldSoft(f), border: `1px solid ${fieldRing(f)}` }}>
        <SlIcon name={f.icon} size={15} sw={1.7} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{field}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span className="mono" style={{ fontSize: 20.5, fontWeight: 800, lineHeight: 1, color: f.text, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{count === 1 ? 'change' : 'changes'}</span>
        </span>
      </span>
    </button>
  );
};

export const FieldChips = ({ byField, fieldFilter, onFieldFilter }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
    {byField.map((b) => (
      <FieldChip
        key={b.field_changed}
        field={b.field_changed}
        count={b.change_count}
        selected={onFieldFilter ? fieldFilter === b.field_changed : false}
        onClick={onFieldFilter ? () => onFieldFilter(fieldFilter === b.field_changed ? null : b.field_changed) : undefined}
      />
    ))}
  </div>
);

/* ─────────────── change-event timeline ─────────────── */

const ProjectChip = ({ name, number }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 21, padding: '0 8px', borderRadius: 6, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', fontSize: 12.5, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden' }}>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
    {number && <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{number}</span>}
  </span>
);

const OldNew = ({ field, oldV, newV }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', textDecoration: 'line-through', textDecorationColor: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmtVal(field, oldV)}</span>
    <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}><SlIcon name="arrow-r" size={13} sw={1.8} /></span>
    <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmtVal(field, newV)}</span>
  </span>
);

const ChangeEvent = ({ ev, showProject, last }) => {
  const f = fieldMeta(ev.field_changed);
  return (
    <div style={{ display: 'flex', gap: 14, position: 'relative' }}>
      <div style={{ flex: '0 0 auto', width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
        <span style={{ width: 11, height: 11, borderRadius: 999, flex: '0 0 auto', background: fieldSoft(f), border: `2px solid ${f.tone}`, zIndex: 1 }} />
        {!last && <span style={{ flex: 1, width: 2, background: 'var(--border-subtle)', marginTop: 3 }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {showProject && ev.project_name && <ProjectChip name={ev.project_name} number={ev.project_number} />}
              <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{ev.task_code || '—'}</span>
              <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.task_name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <FieldBadge field={ev.field_changed} />
              <OldNew field={ev.field_changed} oldV={ev.old_value} newV={ev.new_value} />
            </div>
          </div>
          <span className="mono" style={{ flex: '0 0 auto', fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', paddingTop: 2 }}>{fmtTime(ev.changed_at)}</span>
        </div>
      </div>
    </div>
  );
};

export const Timeline = ({ events, showProject = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    {events.map((ev, i) => (
      <ChangeEvent key={ev.id ?? i} ev={ev} showProject={showProject} last={i === events.length - 1} />
    ))}
  </div>
);

/* ─────────────── slippage table ─────────────── */

const SlipPill = ({ days }) => {
  const tone = days > 14 ? 'var(--danger)' : 'var(--warning)';
  return (
    <span className="mono" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 46, height: 22, padding: '0 9px', borderRadius: 999, fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: tone, background: `color-mix(in oklch, ${tone} 14%, var(--bg-surface))`, border: `1px solid color-mix(in oklch, ${tone} 22%, transparent)`, whiteSpace: 'nowrap' }}>+{days}d</span>
  );
};

const SlipTh = ({ children, align = 'left', w }) => (
  <th style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface-2)', textAlign: align, width: w, padding: '10px 14px', borderBottom: '1px solid var(--border-default)', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{children}</th>
);

export const SlippageTable = ({ rows, showProject = false }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', minWidth: showProject ? 820 : 640, borderCollapse: 'separate', borderSpacing: 0 }}>
      <thead>
        <tr>
          {showProject && <SlipTh>Project</SlipTh>}
          <SlipTh w={108}>Code</SlipTh>
          <SlipTh>Task</SlipTh>
          <SlipTh w={132} align="right">Previous</SlipTh>
          <SlipTh w={132} align="right">New</SlipTh>
          <SlipTh w={92} align="center">Slipped</SlipTh>
          <SlipTh w={170} align="right">When</SlipTh>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const days = dayDiff(r.old_value, r.new_value);
          const big = days > 14;
          return (
            <tr key={r.id ?? i} style={{ borderTop: '1px solid var(--border-subtle)', background: big ? 'color-mix(in oklch, var(--danger) 5%, transparent)' : 'transparent' }}>
              {showProject && <td style={{ padding: '11px 14px', fontSize: 14, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{r.project_name}</td>}
              <td className="mono" style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.task_code || '—'}</td>
              <td style={{ padding: '11px 14px', fontSize: 14.5, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{r.task_name}</td>
              <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 13.5, color: 'var(--text-3)', textDecoration: 'line-through', textDecorationColor: 'var(--text-4)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.old_value)}</td>
              <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 14, fontWeight: 600, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.new_value)}</td>
              <td style={{ padding: '11px 14px', textAlign: 'center' }}><SlipPill days={days} /></td>
              <td className="mono" style={{ padding: '11px 14px', textAlign: 'right', fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.changed_at)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/* ─────────────── card shell + inline empty ─────────────── */

export const Card = ({ header, children, pad = true }) => (
  <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
    {header && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>{header}</div>}
    <div style={{ padding: pad ? 18 : 0 }}>{children}</div>
  </div>
);

export const CardTitle = ({ children }) => <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>{children}</span>;

export const EmptyInline = ({ title, body }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, padding: '32px 24px' }}>
    <div aria-hidden="true" style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <SlIcon name="inbox" size={22} />
    </div>
    <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)' }}>{title}</div>
    <div style={{ fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, maxWidth: 360 }}>{body}</div>
  </div>
);
